import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Paper, Alert, MenuItem,
  LinearProgress
} from "@mui/material";
import { Send, Notifications, People, CheckCircle, CloudUpload, Replay } from "@mui/icons-material";
import API from "../services/api";
import toast from "react-hot-toast";

export default function PushCampaigns() {
  const [stats, setStats] = useState({});
  const [list, setList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleResend = async (id, title) => {
    if (!window.confirm(`"${title}" bildirimi tekrar gönderilsin mi?`)) return;
    try {
      const r = await API.post(`/api/admin/notifications/${id}/resend`);
      toast.success(`Tekrar gönderildi: ${r.data.sent || 0} cihaz, ${r.data.failed || 0} başarısız`);
      load();
    } catch (e) {
      toast.error("Hata: " + (e.response?.data?.error || e.message));
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Maks 5 MB");
    setUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const r = await API.post("/api/admin/campaigns/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setForm({ ...form, image_url: r.data.url });
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error("Yükleme hatası: " + (err.response?.data?.error || err.message));
    } finally { setUploading(false); }
  };
  const [form, setForm] = useState({
    title: "",
    body: "",
    image_url: "",
    click_action: "",
    target_type: "all",
    target_group_id: ""
  });

  const load = async () => {
    try {
      const [s, l, g] = await Promise.all([
        API.get("/api/admin/notifications/stats"),
        API.get("/api/admin/notifications/list"),
        API.get("/api/admin/b2b/groups")
      ]);
      setStats(s.data);
      setList(l.data);
      setGroups(g.data);
    } catch (e) { toast.error("Yüklenemedi"); }
  };

  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.title || !form.body) return toast.error("Başlık ve mesaj zorunlu");
    if (!window.confirm(`Bu bildirim ${stats.active_devices || 0} cihaza gönderilecek. Devam edilsin mi?`)) return;
    setSending(true);
    try {
      const payload = { ...form };
      if (payload.target_type !== "group") delete payload.target_group_id;
      const r = await API.post("/api/admin/notifications/send", payload);
      toast.success(`Gönderildi: ${r.data.sent || 0} cihaz, ${r.data.failed || 0} başarısız`);
      setDialog(false);
      setForm({ title: "", body: "", image_url: "", click_action: "", target_type: "all", target_group_id: "" });
      load();
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      toast.error(msg);
      if (msg.includes("Firebase")) {
        alert("Firebase yapılandırılmamış!\n\n1. Firebase Console'da proje oluştur\n2. Service Account JSON indir\n3. /etc/firebase-service-account.json olarak VPS'e yükle\n4. API'yi yeniden başlat");
      }
    } finally { setSending(false); }
  };

  const fmt = (s) => s ? new Date(s).toLocaleString("tr-TR") : "—";

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>🔔 Push Bildirim Kampanyaları</Typography>

      {!stats.firebase_ready && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠️ Firebase yapılandırılmamış. Push gönderilemez.
          Test mesajları kayıt altına alınır ama cihazlara gönderilmez.
          Firebase Console → Project Settings → Service Accounts → JSON indir → VPS'e <code>/etc/firebase-service-account.json</code> olarak yükle.
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <People sx={{ fontSize: 32, color: '#1565C0' }} />
            <Typography variant="h4" fontWeight={700}>{stats.active_users || 0}</Typography>
            <Typography variant="caption">Aktif Kullanıcı</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Notifications sx={{ fontSize: 32, color: '#9C27B0' }} />
            <Typography variant="h4" fontWeight={700}>{stats.active_devices || 0}</Typography>
            <Typography variant="caption">Aktif Cihaz</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Send sx={{ fontSize: 32, color: '#4CAF50' }} />
            <Typography variant="h4" fontWeight={700}>{stats.total_sent || 0}</Typography>
            <Typography variant="caption">Gönderim Sayısı</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 32, color: '#FF9800' }} />
            <Typography variant="h4" fontWeight={700}>{stats.total_recipients || 0}</Typography>
            <Typography variant="caption">Toplam Alıcı</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<Send />} onClick={() => setDialog(true)} size="large">
          Yeni Bildirim Oluştur
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>Geçmiş Kampanyalar</Typography>
          {list.length === 0 ? (
            <Alert severity="info">Henüz bildirim gönderilmedi</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Tarih</b></TableCell>
                  <TableCell><b>Başlık</b></TableCell>
                  <TableCell><b>Hedef</b></TableCell>
                  <TableCell align="right"><b>Gönderilen</b></TableCell>
                  <TableCell align="right"><b>Başarısız</b></TableCell>
                  <TableCell><b>Durum</b></TableCell>
                  <TableCell align="center"><b>İşlem</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map(n => (
                  <TableRow key={n.id} hover>
                    <TableCell sx={{ fontSize: 12 }}>{fmt(n.sent_at || n.created_at)}</TableCell>
                    <TableCell><b>{n.title}</b><br/><small style={{color:'#888'}}>{n.body?.substring(0,60)}</small></TableCell>
                    <TableCell>{n.target_type}</TableCell>
                    <TableCell align="right">{n.sent_count}</TableCell>
                    <TableCell align="right">{n.failed_count || 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={n.status}
                        size="small"
                        color={n.status === "sent" ? "success" : n.status === "failed" ? "error" : "default"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" startIcon={<Replay />} onClick={() => handleResend(n.id, n.title)} variant="outlined">
                        Tekrar Gönder
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialog} onClose={() => !sending && setDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>📱 Yeni Push Bildirim</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Başlık *"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              fullWidth
              helperText="En fazla 65 karakter (Android bildirim başlığı)"
            />
            <TextField
              label="Mesaj *"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              fullWidth
              multiline
              rows={3}
              helperText="En fazla 240 karakter"
            />
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 2, textAlign: 'center' }}>
              {form.image_url ? (
                <Box>
                  <img src={form.image_url} alt="" style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8 }} />
                  <Box>
                    <Button size="small" onClick={() => setForm({ ...form, image_url: "" })}>Kaldır</Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Button variant="contained" component="label" startIcon={<CloudUpload />} disabled={uploading}>
                    {uploading ? "Yükleniyor..." : "📷 Görsel Yükle (opsiyonel)"}
                    <input type="file" accept="image/*" hidden onChange={handleFile} />
                  </Button>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#888' }}>
                    JPG, PNG, WebP — Maks 5 MB
                  </Typography>
                </Box>
              )}
            </Box>
            <TextField
              label="Yönlendirme (opsiyonel)"
              value={form.click_action}
              onChange={(e) => setForm({ ...form, click_action: e.target.value })}
              fullWidth
              placeholder="ör: /products/123 veya /campaign/yaz"
              helperText="Bildirime tıklanınca app içinde açılacak yer"
            />
            <TextField
              select
              label="Hedef Kitle"
              value={form.target_type}
              onChange={(e) => setForm({ ...form, target_type: e.target.value })}
              fullWidth
            >
              <MenuItem value="all">Tüm Kullanıcılar ({stats.active_devices || 0} cihaz)</MenuItem>
              <MenuItem value="group">Belirli Müşteri Grubu</MenuItem>
            </TextField>
            {form.target_type === "group" && (
              <TextField
                select
                label="Müşteri Grubu *"
                value={form.target_group_id}
                onChange={(e) => setForm({ ...form, target_group_id: e.target.value })}
                fullWidth
              >
                {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name} ({g.user_count} kullanıcı)</MenuItem>)}
              </TextField>
            )}

            {sending && <LinearProgress />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)} disabled={sending}>İptal</Button>
          <Button variant="contained" startIcon={<Send />} onClick={send} disabled={sending}>
            {sending ? "Gönderiliyor..." : "Yayınla"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
